import React from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import {
  AddBodyCell,
  BodyCell,
  BodyRow,
  ExpandedCell,
  HeadCell,
  LoadMoreFooter,
  SelectionCell,
  SortButton,
  Table,
  TableHead,
  TableWrap,
} from "./DataTable.styles";
import { Button } from "@magicbi/ui-library";
import { Checkbox } from "../../atoms/Checkbox/Checkbox";
import type { DataTableProps } from "./DataTable.types";

export const DataTable = <Row,>({
  columns,
  rows,
  rowKey,
  chromeless = false,
  stickyHeader = false,
  density = "comfortable",
  tableLayoutFixed = false,
  ctaRow,
  addRow,
  isRowSelected,
  sort,
  onSort,
  onRowClick,
  headAppearance = "default",
  selectedTone = true,
  ariaLabel,
  selection,
  loadMore,
  expandedRow,
  className,
}: DataTableProps<Row>): React.JSX.Element => {
  const selectedKeySet = new Set(selection?.selectedKeys ?? []);
  const keyOf = selection?.rowKey ?? rowKey;
  const selectedCount = rows.filter((row) => selectedKeySet.has(keyOf(row))).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const columnCount = columns.length + (selection ? 1 : 0);
  return (
  <TableWrap className={className} $chromeless={chromeless}>
    <Table $stickyHeader={stickyHeader} $layoutFixed={tableLayoutFixed} aria-label={ariaLabel}>
      <TableHead $sticky={stickyHeader} $plain={headAppearance === "plain"} $density={density}>
        <tr>
          {selection ? (
            <HeadCell
              $align="center"
              $width="2.75rem"
              $stickyHeader={stickyHeader}
              $density={density}
              $appearance={headAppearance}
            >
              <Checkbox
                checked={allSelected}
                indeterminate={selectedCount > 0 && !allSelected}
                disabled={rows.length === 0}
                onChange={(next) => selection.onToggleAll(next)}
                ariaLabel={selection.label}
              />
            </HeadCell>
          ) : null}
          {columns.map((column) => {
            const order = sort?.key === column.key ? sort.order : null;
            const Mark = order === "asc" ? ArrowUp : order === "desc" ? ArrowDown : ChevronsUpDown;
            return (
              <HeadCell
                key={column.key}
                $align={column.align ?? "left"}
                $width={column.width}
                $stickyHeader={stickyHeader}
                $density={density}
                $hideNarrow={column.hideOnNarrow}
                $appearance={headAppearance}
                aria-sort={
                  column.sortable
                    ? order === "asc"
                      ? "ascending"
                      : order === "desc"
                        ? "descending"
                        : "none"
                    : undefined
                }
              >
                {column.sortable && onSort ? (
                  <SortButton type="button" $active={order !== null} onClick={() => onSort(column.key)}>
                    {column.header}
                    <Mark aria-hidden="true" />
                  </SortButton>
                ) : (
                  column.header
                )}
              </HeadCell>
            );
          })}
        </tr>
      </TableHead>
      <tbody>
        {addRow ? (
          <BodyRow data-testid="data-table-add-row" $density={density}>
            {selection ? <AddBodyCell $align="center" $bodyVariant={3} $emphasis="medium" /> : null}
            {columns.map((col) => (
              <AddBodyCell
                key={col.key}
                $align={col.align ?? "left"}
                $bodyVariant={3}
                $emphasis="medium"
                $width={col.width}
              >
                {addRow[col.key] ?? null}
              </AddBodyCell>
            ))}
          </BodyRow>
        ) : ctaRow ? (
          <BodyRow data-testid="data-table-cta-row" $density={density}>
            <BodyCell
              $density={density}
              $plain={headAppearance === "plain"}
              colSpan={columnCount}
              $align="left"
              $bodyVariant={3}
              $emphasis="medium"
            >
              {ctaRow}
            </BodyCell>
          </BodyRow>
        ) : null}
        {rows.map((row) => {
          const selected = isRowSelected?.(row) ?? false;
          return (
            <React.Fragment key={rowKey(row)}>
            <BodyRow
              $selected={selected && selectedTone}
              $density={density}
              $clickable={Boolean(onRowClick)}
              data-selected={selected ? "true" : undefined}
              onClick={
                onRowClick
                  ? (event) => {
                      // A link or button inside the row handles its own click.
                      if ((event.target as HTMLElement).closest("a,button")) return;
                      onRowClick(row);
                    }
                  : undefined
              }
            >
              {selection ? (
                <SelectionCell
                  $density={density}
                  $plain={headAppearance === "plain"}
                  $align="center"
                  $bodyVariant={3}
                  $emphasis="medium"
                  $width="2.75rem"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Checkbox
                    checked={selectedKeySet.has(keyOf(row))}
                    onChange={() => selection.onToggle(keyOf(row))}
                    ariaLabel={selection.rowLabel ? selection.rowLabel(row) : selection.label}
                  />
                </SelectionCell>
              ) : null}
              {columns.map((column) => (
                <BodyCell
                  key={column.key}
                  $align={column.align ?? "left"}
                  $bodyVariant={column.bodyVariant ?? 3}
                  $emphasis={column.emphasis ?? "medium"}
                  $width={column.width}
                  $noWrap={column.noWrap}
                  $hideNarrow={column.hideOnNarrow}
                  $density={density}
                  $plain={headAppearance === "plain"}
                >
                  {column.render(row)}
                </BodyCell>
              ))}
            </BodyRow>
            {expandedRow?.isOpen(row) ? (
              <tr data-testid="data-table-expanded-row">
                <ExpandedCell colSpan={columnCount}>{expandedRow.render(row)}</ExpandedCell>
              </tr>
            ) : null}
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
    {loadMore && loadMore.hasMore ? (
      <LoadMoreFooter data-testid="data-table-load-more">
        <Button type="button" variant="secondary" size="sm" loading={loadMore.loading} disabled={loadMore.loading} onClick={loadMore.onClick}>
          {loadMore.label}
        </Button>
      </LoadMoreFooter>
    ) : null}
  </TableWrap>
  );
};

export default DataTable;
